import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { PACKS, getRandomMuse } from '../../../data/packs';
import { Muse } from '../../../types';
import { useNavigate } from 'react-router-dom';

type DemoState = {
  wallet: {
    balance: number;
  };
  inventory: Muse[];
  equippedMuseId: string | null;
  packToOpen: Muse[] | null;
  isLockedModalOpen: boolean;
  addMuseCash: (amount: number) => void;
  buyPack: (packId: string, price: number) => void;
  setEquippedMuse: (museId: string) => void;
  clearPackToOpen: () => void;
  recordProgress: (gameId: string, progress: any) => void;
  showLockedModal: () => void;
  hideLockedModal: () => void;
};

const DemoContext = createContext<DemoState | null>(null);

const starterMuses = [
  PACKS['Friendly Pack 1'].muses[0], // Songly
  PACKS['Sun Pack'].muses[0],
  PACKS['Night Pack'].muses[0],
  PACKS['Cyclops Pack'].muses[0],
];

const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [wallet, setWallet] = useState({ balance: 500 });
  const [inventory, setInventory] = useState<Muse[]>(starterMuses);
  const [equippedMuseId, setEquippedMuseId] = useState<string | null>(starterMuses[0].name);
  const [packToOpen, setPackToOpen] = useState<Muse[] | null>(null);
  const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timers = [
      setTimeout(() => setIsLockedModalOpen(true), 35000), // 35 seconds
      setTimeout(() => setIsLockedModalOpen(true), 95000), // 35s + 60s
      setTimeout(() => setIsLockedModalOpen(true), 215000) // 95s + 120s
    ];

    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    try {
      const savedState = localStorage.getItem('demo:state');
      if (savedState) {
        const { wallet, inventory, equippedMuseId } = JSON.parse(savedState);
        if (wallet) setWallet(wallet);
        if (inventory) setInventory(inventory);
        if (equippedMuseId) setEquippedMuseId(equippedMuseId);
      }
    } catch (error) {
      console.error("Failed to load demo state from localStorage", error);
    }
  }, []);

  useEffect(() => {
    try {
      const stateToSave = { wallet, inventory, equippedMuseId };
      localStorage.setItem('demo:state', JSON.stringify(stateToSave));
    } catch (error) {
      console.error("Failed to save demo state to localStorage", error);
    }
  }, [wallet, inventory, equippedMuseId]);

  const addMuseCash = (amount: number) => {
    setWallet(prev => ({ ...prev, balance: prev.balance + amount }));
  };

  const buyPack = (packId: string, price: number) => {
    if (wallet.balance >= price) {
      setWallet(prev => ({ ...prev, balance: prev.balance - price }));
      const newMuses = Array.from({ length: 3 }, () => getRandomMuse(packId));
      setInventory(prev => [...prev, ...newMuses]);
      setPackToOpen(newMuses);
      navigate('/demo/pack-opening');
    } else {
      void window.appAlert("Not enough MuseCash!");
    }
  };

  const setEquippedMuse = (museId: string) => {
    setEquippedMuseId(museId);
  };
  
  const clearPackToOpen = () => {
    setPackToOpen(null);
  };

  const showLockedModal = () => setIsLockedModalOpen(true);
  const hideLockedModal = () => setIsLockedModalOpen(false);

  const recordProgress = (gameId: string, progress: any) => {
    // This is a placeholder for game progress tracking
    console.log(`Recording progress for ${gameId}`, progress);
  };

  const value = {
    wallet,
    inventory,
    equippedMuseId,
    packToOpen,
    isLockedModalOpen,
    addMuseCash,
    buyPack,
    setEquippedMuse,
    clearPackToOpen,
    recordProgress,
    showLockedModal,
    hideLockedModal,
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};

export default DemoProvider;


